import React, { useState, useEffect } from 'react';
import { Search, Download, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function ProcesoNotas({ filters }) {
    const [estudiantes, setEstudiantes] = useState([]);
    const [notas, setNotas] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [cargadoNotasHabilitado, setCargadoNotasHabilitado] = useState(true);
    const [periodoInfo, setPeriodoInfo] = useState(null);
    const [inscripcionCerrada, setInscripcionCerrada] = useState(false);
    const [mensajeCierre, setMensajeCierre] = useState('');

    const semanas = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

    useEffect(() => {
        verificarPeriodoCargadoNotas();
    }, []);

    useEffect(() => {
        if (filters.id_asignatura) {
            loadEstudiantes();
            loadNotas();
        }
    }, [filters.id_asignatura, filters.id_paralelo, filters.gestion, filters.periodo]);

    const verificarPeriodoCargadoNotas = async () => {
        try {
            const paramName = 'Cargado de notas Licenciatura > Proceso (docente)';
            const response = await fetch(`${API_URL}/direccion/parametrizaciones/verificar/${encodeURIComponent(paramName)}`);
            const data = await response.json();
            
            setCargadoNotasHabilitado(data.habilitado);
            setPeriodoInfo(data);

            if (!data.habilitado) {
                setInscripcionCerrada(true);
                setMensajeCierre(data.mensaje || 'El periodo de cargado de notas de proceso se encuentra cerrado.');
            } else {
                setInscripcionCerrada(false);
            }
        } catch (error) {
            console.error('Error verificando periodo:', error);
        }
    };

    const loadEstudiantes = async () => {
        try {
            setLoading(true);
            const query = new URLSearchParams({
                id_asignatura: filters.id_asignatura,
                ...(filters.id_paralelo && { id_paralelo: filters.id_paralelo })
            });

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/docente/estudiantes?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setEstudiantes(data.estudiantes || []);
        } catch (error) {
            console.error('Error loading estudiantes:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadNotas = async () => {
        try {
            const query = new URLSearchParams({
                id_asignatura: filters.id_asignatura,
                ...(filters.id_paralelo && { id_paralelo: filters.id_paralelo }),
                gestion: filters.gestion,
                periodo: filters.periodo
            });

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/docente/notas-proceso?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            // Organizar por estudiante y semana
            const notasMap = {};
            (data.notas || []).forEach(n => {
                if (!notasMap[n.id_estudiante]) {
                    notasMap[n.id_estudiante] = {};
                }
                notasMap[n.id_estudiante][n.semana] = n.nota;
            });
            setNotas(notasMap);
        } catch (error) {
            console.error('Error loading notas:', error);
        }
    };

    const handleNotaChange = (id_estudiante, semana, valor) => {
        setNotas(prev => ({
            ...prev,
            [id_estudiante]: {
                ...prev[id_estudiante],
                [semana]: valor ? parseFloat(valor) : null
            }
        }));
    };

    const handleGuardar = async () => {
        if (!cargadoNotasHabilitado) {
            alert(`El periodo de cargado de notas no está habilitado.${periodoInfo?.fecha_inicio ? `\nFecha inicio: ${new Date(periodoInfo.fecha_inicio).toLocaleDateString()}` : ''}${periodoInfo?.fecha_fin ? `\nFecha fin: ${new Date(periodoInfo.fecha_fin).toLocaleDateString()}` : ''}`);
            return;
        }

        try {
            setSaving(true);

            // Recopilar todas las notas a guardar
            const notasAGuardar = [];
            estudiantes.forEach(est => {
                const notasEst = notas[est.id_estudiante] || {};
                semanas.forEach(semana => {
                    if (notasEst[semana] !== undefined && notasEst[semana] !== null) {
                        notasAGuardar.push({
                            id_estudiante: est.id_estudiante,
                            id_asignatura: filters.id_asignatura,
                            id_paralelo: filters.id_paralelo || null,
                            semana,
                            nota: notasEst[semana],
                            gestion: filters.gestion,
                            periodo: filters.periodo
                        });
                    }
                });
            });

            if (notasAGuardar.length === 0) {
                alert('No hay notas para guardar');
                setSaving(false);
                return;
            }

            console.log(`Guardando ${notasAGuardar.length} notas...`);

            // Usar el endpoint bulk para mejor rendimiento
            const response = await fetch(`${API_URL}/docente/notas-proceso/bulk`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    notas: notasAGuardar
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                
                // Status 207 means partial success
                if (response.status === 207) {
                    console.log('Guardado parcial:', errorData);
                    const mensaje = `⚠️ Guardado parcial:\n\n` +
                        `✅ Exitosas: ${errorData.exitosas || 0}\n` +
                        `❌ Fallidas: ${errorData.fallidas || 0}\n\n` +
                        (errorData.errores && errorData.errores.length > 0 ? 
                            `Primeros errores:\n${errorData.errores.slice(0, 3).map(e => 
                                `- Estudiante ${e.nota?.id_estudiante || '?'}, Semana ${e.nota?.semana || '?'}: ${e.error}`
                            ).join('\n')}` : '');
                    
                    alert(mensaje);
                    await loadNotas();
                    return;
                }
                
                throw new Error(errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            console.log('Bulk save response:', data);
            
            alert(`✅ Notas guardadas exitosamente!\n\n${notasAGuardar.length} notas procesadas correctamente.`);
            await loadNotas();
        } catch (error) {
            console.error('Error saving notas:', error);
            const mensajeError = error.message || 'Error desconocido';
            alert(`Error al guardar notas: ${mensajeError}\n\nRevise la consola del navegador para más detalles.`);
        } finally {
            setSaving(false);
        }
    };

    const filteredEstudiantes = estudiantes.filter(est =>
        searchTerm === '' ||
        est.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        est.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-4">
            {/* Mensaje si inscripciones cerradas */}
            {inscripcionCerrada ? (
                <div className="bg-white shadow-xl rounded-2xl p-8 mb-6 text-center border border-red-100">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-800 mb-2">Periodo de Calificación Cerrado</h2>
                    <p className="text-gray-600 mb-4">{mensajeCierre}</p>
                </div>
            ) : (
                <>
            {/* Barra de herramientas */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h3 className="text-lg font-semibold text-gray-800">Notas de Proceso</h3>
                    <p className="text-sm text-gray-600">Periodo {filters.periodo} - Gestión {filters.gestion}</p>
                </div>

                <div className="flex items-center gap-2">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Buscar estudiante..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    </div>

                    <button
                        onClick={() => alert('Descarga funcionalidad')}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                        <Download size={18} />
                        Descargar
                    </button>
                </div>
            </div>

            {/* Tabla */}
            {loading ? (
                <div className="p-8 text-center text-gray-500">Cargando...</div>
            ) : filteredEstudiantes.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                    {filters.id_asignatura ? 'No hay estudiantes' : 'Selecciona una asignatura'}
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b">
                                    <th className="p-2 text-left text-sm font-semibold text-gray-700 sticky left-0 bg-gray-50">No</th>
                                    <th className="p-2 text-left text-sm font-semibold text-gray-700 sticky left-12 bg-gray-50">Código</th>
                                    {semanas.map(sem => (
                                        <th key={sem} className="p-2 text-center text-sm font-semibold text-gray-700">S{sem}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEstudiantes.map((est, idx) => {
                                    const notasEst = notas[est.id_estudiante] || {};

                                    return (
                                        <tr key={est.id_estudiante} className="border-b hover:bg-gray-50">
                                            <td className="p-2 text-sm sticky left-0 bg-white">{idx + 1}</td>
                                            <td className="p-2 text-sm font-medium sticky left-12 bg-white">{est.codigo}</td>

                                            {semanas.map(semana => (
                                                <td key={semana} className="p-2">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="0.01"
                                                        value={notasEst[semana] || ''}
                                                        onChange={(e) => handleNotaChange(est.id_estudiante, semana, e.target.value)}
                                                        className="w-16 px-2 py-1 text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-end">
                        <button
                            onClick={handleGuardar}
                            disabled={saving || filteredEstudiantes.length === 0}
                            className="flex items-center gap-2 px-6 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Save size={18} />
                            {saving ? 'Guardando...' : 'Guardar Notas'}
                        </button>
                    </div>
                </>
            )}
        </>
    )}
</div>
    );
}
